import moment from "moment";
import React, { createContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import CountriesList from "../constants/countries";
import docTypes from "../constants/docTypes";
import {
  buildPaxPayload,
  collectMaskedHints,
  stripMaskedValues
} from "../helpers/maskedFields";
import { reportErrorToNewRelic } from "../helpers/newRelic";
import { getAuthErrorInfo } from "../helpers/authErrors";
import { pathCid, replaceCidInPath } from "../helpers/authQuery";
import { apiLanguage } from "../helpers/documentUpload";
import { hardNavigate } from "../helpers/navigation";
import { takeBootAuth } from "../helpers/bootAuth";
import { rememberLoginDestination } from "../helpers/loginDestination";
import {
  clearSession,
  clearToken,
  clearUserData,
  getStoredUserData,
  getToken,
  setPendingNotice,
  setStoredUserData,
  setToken,
  takePendingNotice
} from "../helpers/session";
import AuthService from "../services/AuthService";
import httpService, { isRevokedSession } from "../services/HttpService";

export const GeneralContext = createContext();

const GeneralContextProvider = props => {
  const [UserData, setUserData] = useState();
  const [Authenticated, setAuthenticated] = useState(false);
  const [PosData, setPosData] = useState({});
  const [ProductData, setProductData] = useState({});
  const [CountriesData, setCountriesData] = useState({ List: CountriesList });
  const [Loading, setLoading] = useState(true);
  const [GeneralError, setGeneralError] = useState(false);
  const [CIDData, setCIDData] = useState();
  const [MenuData, setMenuData] = useState();
  const [CurrentPos, setCurrentPos] = useState();
  const [PaxsData, setPaxsDataInternal] = useState();
  const [AuthNotice, setAuthNotice] = useState(null);
  const [LoginPrefill, setLoginPrefill] = useState(null);
  // Valores enmascarados por api2 ({ [paxId]: { campo: "****1234" } }): se
  // muestran como pista y nunca se reenvían.
  const maskedHints = useRef({});
  const [LocalPaxData, setLocalPaxDataInternal] = useState({});
  const [ReviewMode, setReviewMode] = useState(false);
  const [Validating, setValidating] = useState(0);
  const [DocTypes, setDocTypes] = useState(docTypes);
  const [CIDSignature, setCIDSignatureInternal] = useState();
  const [SIRECities, setSIRECities] = useState();
  const [BookingReady, setBookingReady] = useState([]);
  const [CurrentLng, setCurrentLng] = useState(props.language);
  const [LngLink, setLngLink] = useState(
    CurrentLng !== "es" ? `/${CurrentLng}` : ""
  );
  const [PreviousPage, setPreviousPage] = useState();

  const history = useHistory();
  const sessionExpired = useRef(false);

  const loginPath = () =>
    `${LngLink}${process.env.REACT_APP_ROOT_PATH_LINK}/login`;
  const rootPath = () => `${LngLink}${process.env.REACT_APP_ROOT_PATH_LINK}/`;

  // Errores de pedidos con sesión: 401 (y 500 al cargar datos) -> error
  // general y vuelta al inicio. Se ignora si no hay respuesta (error de red) y
  // si la sesión ya se dio por vencida (el login ya se está cargando).
  const onAuthFailure = (err, statuses) => {
    const status = err && err.response && err.response.status;
    if (!status || statuses.indexOf(status) === -1) return;
    if (sessionExpired.current) return;
    generalError();
    hardNavigate(rootPath());
  };

  // api2 responde 401 `unauthorized` cuando el token del huésped fue revocado
  // (logout / versión de token): se trata como sesión vencida.
  useEffect(() => {
    const onExpired = () => {
      if (sessionExpired.current) return;
      sessionExpired.current = true;
      clearSession();
      setPendingNotice({ code: "unauthorized", status: 401, retryAfter: null });
      setAuthenticated(false);
      // Navegación completa: descarta todo el estado de React.
      hardNavigate(loginPath());
    };
    window.addEventListener("pxsession:expired", onExpired);
    return () => window.removeEventListener("pxsession:expired", onExpired);
  }, [LngLink]);

  useEffect(() => {
    initialize();
  }, []);

  async function initialize() {
    // Caché de la lista de dominios de api1 (pos/getDomains) que ya no se usa:
    // se borra para que no quede ocupando lugar.
    localStorage.removeItem("PxDomains");
    fetchPosData().then(res => {
      preloadAuth(res);
    });
  }

  const setPaxsData = data => {
    if (data && Array.isArray(data.list)) {
      data.list.forEach(pax => {
        const hints = collectMaskedHints(pax);
        if (pax && Object.keys(hints).length)
          maskedHints.current[String(pax.paxid)] = {
            ...maskedHints.current[String(pax.paxid)],
            ...hints
          };
      });
    }
    setPaxsDataInternal(data);
  };

  const getMaskedHint = (paxId, field) =>
    (maskedHints.current[String(paxId)] || {})[field];

  // Los valores "****" nunca entran al estado del formulario ni al borrador.
  const sanitizeLocalPaxData = data => {
    const clean = {};
    Object.keys(data || {}).forEach(id => {
      clean[id] = stripMaskedValues(data[id]);
    });
    return clean;
  };

  const setLocalPaxData = data => {
    const clean = sanitizeLocalPaxData(data);
    localStorage.setItem(
      `LocalPaxData_${CIDData.IDInbox}`,
      JSON.stringify(clean)
    );
    setLocalPaxDataInternal(clean);
  };
  const setCIDSignature = data => {
    localStorage.setItem(
      `CIDSignature_${CIDData.IDInbox}`,
      JSON.stringify(data)
    );
    setCIDSignatureInternal(data);
  };

  const generalError = () => {
    // Sesión vencida: ya se mandó al login con su mensaje.
    if (sessionExpired.current) return;
    toast.error(<FormattedMessage id="main.generalError" />);
    setGeneralError(true);
    history.push("/app/error");
    setLoading(false);
    // Limpiamos la sesión (token, UserData y borradores) pero preservamos lo
    // que el huésped cargó a mano (datos de pax y firma) SOLO de la reserva de
    // la sesión: en un dispositivo compartido no deben sobrevivir borradores
    // con PII de otras reservas.
    const storedUser = getStoredUserData();
    const activeCid =
      storedUser && storedUser.CID ? String(storedUser.CID) : null;

    const preserved = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const isActiveDraft =
        (key.startsWith("LocalPaxData_") || key.startsWith("CIDSignature_")) &&
        activeCid !== null &&
        key.split("_")[1] === activeCid;
      if (key === "CurrentPos" || isActiveDraft) {
        preserved[key] = localStorage.getItem(key);
      }
    }
    localStorage.clear();
    clearToken();
    clearUserData();
    Object.entries(preserved).forEach(([key, value]) =>
      localStorage.setItem(key, value)
    );
  };

  async function getCountriesData() {
    let countryData = await fetchData("CountriesData", CurrentLng);
    if (countryData.Response === 1 || countryData.response === 1)
      setCountriesData(countryData);
  }

  async function cancelBookingFn() {
    if (!CIDData.IDInbox) throw new Error("No CID provided");
    let ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_CANCEL_BOOKING_EP}${CIDData.IDInbox}`;
    return httpService.post(ep, {}).catch(err => {
      reportErrorToNewRelic("cancelBookingFn", err);
    });
  }

  async function createEmptyPax() {
    let ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_PAX_CREATE}`;
    let newPaxObj = {
      hotel_company_id: CIDData.empresa,
      cid: CIDData.IDInbox
    };
    return httpService
      .post(ep, newPaxObj, { "Content-Type": "application/json" })
      .then(res => {
        if (!res.data) throw new Error("Corrupt or empty response");
        return res.data;
      })
      .catch(err => {
        onAuthFailure(err, [401]);
        throw new Error(err);
      });
  }

  async function submitPaxData(paxId) {
    if (!paxId) throw new Error("PaxId Missing");
    let ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_PAX_UPDATE_EP1}${CIDData.IDInbox}${process.env.REACT_APP_PAX_UPDATE_EP2}${paxId}`;
    return httpService
      .put(ep, buildPaxPayload(LocalPaxData[paxId], maskedHints.current[String(paxId)]), {
        "Content-Type": "application/json"
      })
      .then(res => {
        if (!res.data) throw new Error("Corrupt or empty response");
        return res.data;
      })
      .catch(err => {
        reportErrorToNewRelic("submitPaxData", err);
        onAuthFailure(err, [401]);
        throw new Error(err);
      });
  }

  async function fileUpload(side, file, paxId) {
    if (side !== "frente" && side !== "dorso") return false;
    if (!file) return false;
    if (!paxId) return false;
    const url = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_DOCS_ANALYZE}${paxId}`;
    const formData = new FormData();
    formData.append("image", file, `${side}.jpg`);
    formData.append("idcardfront", side);
    // Idioma en que api2 devuelve el motivo de un rechazo o la advertencia.
    formData.append("lang", apiLanguage(CurrentLng));
    const config = {
      headers: {
        "content-type": "multipart/form-data"
      },
      // PhotoUpload muestra el motivo: sin esto el interceptor lo repetiría en otro toast.
      skipErrorToast: true
    };
    return httpService
      .post(url, formData, config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        // Con skipErrorToast el interceptor no da por vencida la sesión revocada: se hace acá.
        if (isRevokedSession(err))
          window.dispatchEvent(new Event("pxsession:expired"));
        onAuthFailure(err, [401]);
        // Se relanza el error original: PhotoUpload lee el motivo de `err.response.data`.
        throw err;
      });
  }

  async function fetchData(dataType, ...args) {
    let ep = "";
    let lsString = "";
    let lsStore = true;
    switch (dataType) {
      case "MenuData":
        if (!args[1]) throw new Error("Missing ProductID in fetchMenuData");
        if (!args[0]) throw new Error("Missing CID in fetchMenuData");
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_MENU_EP}?cid=${args[0]}&empresa=${args[1]}&market=true&type=WebApp`;
        lsString = `MenuData_${args[1]}`;
        break;
      case "CIDData":
        if (!args[0]) throw new Error("Missing CID in fetchCIDData");
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_BOOKING_INFO_EP}`;
        lsString = `CIDData_${args[0]}`;
        break;
      case "PosInfo":
        if (!args[0]) throw new Error("Missing POS in fetchPosInfo");
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_POS_INFO_EP}${args[0]}`;
        lsString = `PosInfo`;
        break;
      case "ProductData":
        if (!args[0]) throw new Error("Missing POS in fetchProductData");
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_PRODUCT_INFO_EP}${args[0]}`;
        lsString = `ProductData`;
        break;
      case "CountriesData":
        if (!args[0]) args[0] = "es";
        ep = `${process.env.REACT_APP_APIOLD_EP}${process.env.REACT_APP_COUNTRIES_LIST_EP}?lng=${CurrentLng}`;
        lsString = `CountriesData`;
        break;
      case "PolicyData":
        if (!CurrentLng) CurrentLng = "es";
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_POLICIES_INFO_EP}${args[0]}?lng=${CurrentLng}`;
        lsString = `PolicyData_${args[0]}`;
        break;
      case "PaxsData":
        if (!args[0]) throw new Error("Missing CID in fetchPaxsData");
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_PAXS_EP}${args[0]}`;
        lsString = `PaxsData`;
        lsStore = false;
        break;
      case "SIRECities":
        ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_SIRE_CITIES_EP}`;
        lsString = `SIRECities`;
        break;
      default:
        throw new Error("Undefined DataType");
    }
    if (!ep) throw new Error("Undefined DataType");
    let lsData = JSON.parse(localStorage.getItem(lsString));
    if (lsData) {
      if (
        !lsData.cacheTime ||
        !moment(lsData.cacheTime).isValid() ||
        moment().diff(moment(lsData.cacheTime), "days") > 7
      )
        lsData = null;
    }
    return lsData && !(lsData.response === 0)
      ? lsData
      : httpService
          .get(ep)
          .then(res => {
            if (!res.data) throw new Error("Corrupt or empty " + dataType);
            if (
              lsStore &&
              !(res.data.Response === 0) &&
              !(res.data.response === 0)
            ) {
              res.data.cacheTime = moment().format();
              localStorage.setItem(lsString, JSON.stringify(res.data));
              if (lsString === "ProductData") {
                localStorage.setItem(lsString, JSON.stringify(res.data.info));
                return res.data.info;
              }
            }
            return res.data;
          })
          .catch(err => {
            reportErrorToNewRelic(dataType, err);
            onAuthFailure(err, [401, 500]);
            if (dataType !== "PolicyData") throw new Error(err);
          });
  }

  // api2 resuelve el POS del dominio actual (por `?pos=`, Domain,
  // AlternativeDomains o URL): el navegador ya no baja la lista de dominios de
  // todos los hoteles (api1 pos/getDomains). Devuelve { pos, matchedBy }, con
  // pos "" si no hay coincidencia (404) o si el pedido falla por lo que sea.
  async function resolvePosByDomain(host, pos) {
    const params = new URLSearchParams({ host });
    if (pos) params.set("pos", pos);
    const ep = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_RESOLVE_DOMAIN_EP}?${params.toString()}`;
    return httpService
      .get(ep, { skipErrorToast: true })
      .then(res => {
        const data = res && res.status === 200 && res.data && res.data.data;
        if (!data || typeof data.pos !== "string" || !data.pos)
          return { pos: "", matchedBy: null };
        return { pos: data.pos, matchedBy: data.matched_by || null };
      })
      .catch(err => {
        // 404 = ningún POS para este dominio: no es un error de red.
        if (!(err && err.response && err.response.status === 404))
          reportErrorToNewRelic("resolvePosByDomain", err);
        return { pos: "", matchedBy: null };
      });
  }

  async function fetchPosData() {
    try {
      let productData = JSON.parse(localStorage.getItem("ProductData"));
      if (productData) {
        if (
          !productData.cacheTime ||
          !moment(productData.cacheTime).isValid() ||
          moment().diff(moment(productData.cacheTime), "days") > 7
        )
          productData = null;
      }
      let posData = JSON.parse(localStorage.getItem("PosInfo"));
      if (posData) {
        if (
          !posData.cacheTime ||
          !moment(posData.cacheTime).isValid() ||
          moment().diff(moment(posData.cacheTime), "days") > 7
        )
          posData = null;
      }
      if (posData && productData) {
        setPosData(posData);
        setProductData(productData);
        window.productData = productData;
        return { posData, productData };
      }
      let urlparams = new URLSearchParams(window.location.search);
      let pos = "";
      if (urlparams.get("pos")) pos = urlparams.get("pos").trim();
      const currentHost = window.location.hostname.toLowerCase();
      if (currentHost.indexOf("hoteldemo.com.ar") !== -1) {
        pos = currentHost.split(".")[0];
      }
      const resolved = await resolvePosByDomain(currentHost, pos);
      // El Pos de api2 viene con el case canónico. Si no encuentra nada se sigue
      // con el `?pos=` crudo, y la coincidencia por URL (que puede repetirse
      // entre POS, ej secure.pxsol.com) nunca pisa un `?pos=` explícito: igual
      // que con la lista de getDomains.
      if (resolved.pos && !(pos && resolved.matchedBy === "url"))
        pos = resolved.pos;
      if (!pos) pos = CurrentPos;
      if (!pos) pos = localStorage.getItem("CurrentPos");
      if (!pos) {
        reportErrorToNewRelic("fetchPosData", "POS not defined");
        generalError();
        throw new Error("POS not defined");
      }
      setCurrentPos(pos);
      localStorage.setItem("CurrentPos", pos);
      posData = await fetchData("PosInfo", pos);
      setPosData(posData);
      if (posData.DefaultProductID) {
        productData = await fetchData("ProductData", posData.DefaultProductID);
        setProductData(productData);
        window.productData = productData;
      } else {
        productData = {};
        console.warn("No default product ID for this POS");
      }

      return { posData, productData };
    } catch (err) {
      console.error("error fetchPosData2");
      generalError();
    }
  }

  async function preloadAuth(appdata) {
    let token = getToken();
    let userData = getStoredUserData();
    sessionExpired.current = false;
    // Aviso que sobrevivió a una navegación completa (sesión vencida, logout).
    const pendingNotice = takePendingNotice();
    if (pendingNotice) setAuthNotice(pendingNotice);
    // El token y los parámetros viejos ya se sacaron de la URL al arrancar
    // (index.js); acá sólo se consumen.
    const { accessToken, legacy } = takeBootAuth();
    // Ruta de entrada: después de cada setState la app puede haber redirigido
    // ya al login (PrivateRoute), así que el destino sale de acá.
    const entryPath = window.location.pathname;

    if (accessToken) {
      // Link con token (mail, WhatsApp, "compartir"): se canjea por una sesión.
      try {
        const auth = await AuthService.exchangeAccessToken(accessToken);
        if (!auth || !auth.token) {
          const e = new Error("invalid_token");
          e.code = "invalid_token";
          throw e;
        }
        // El CID lo informa SÓLO api2 (nunca se deduce de la URL). Si el canje
        // no lo trae, se pregunta con la sesión recién creada.
        let cid = String(auth.cid || "").replace(/[^0-9]/g, "");
        if (!cid) {
          setToken(auth.token);
          const info = await httpService.get(
            `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_BOOKING_INFO_EP}`,
            { skipErrorToast: true }
          );
          cid = String((info && info.data && info.data.IDInbox) || "").replace(
            /[^0-9]/g,
            ""
          );
        }
        if (!cid) {
          const e = new Error("invalid_token");
          e.code = "invalid_token";
          throw e;
        }
        userData = {
          CID: cid,
          LastName: auth.apellido || "",
          CompanyID: appdata?.posData?.CompanyPos
        };
        AuthService.storeSession(auth.token, userData);
        sessionExpired.current = false;
        token = auth.token;
        // Si la ruta trae otro CID, se corrige al que informó api2.
        const urlCid = pathCid(window.location.pathname);
        if (urlCid && urlCid !== cid) {
          const fixed = replaceCidInPath(window.location.pathname, cid);
          history.replace(fixed + window.location.search + window.location.hash);
        }
      } catch (error) {
        clearToken();
        clearUserData();
        setAuthNotice(getAuthErrorInfo(error));
        setAuthenticated(false);
        setLoading(false);
        // Un link de cancelación vencido vuelve a la cancelación después del login.
        rememberLoginDestination(entryPath);
        history.replace(loginPath());
        return false;
      }
    } else if (legacy) {
      // Links viejos con cid/apellido: NO inician sesión. Si ya hay una sesión
      // de esa misma reserva se sigue con ella; si no, se precarga el login
      // (que exige captcha) y se descarta cualquier otra sesión. La empresa
      // sale siempre del POS (el ?c= viejo se ignora).
      const sameSession =
        token && userData && String(userData.CID) === legacy.cid;
      if (!sameSession) {
        clearToken();
        clearUserData();
        token = null;
        userData = null;
        setLoginPrefill({ cid: legacy.cid, lastname: legacy.lastname });
        setAuthenticated(false);
        setLoading(false);
        // Un link viejo a /app/cancel vuelve a la cancelación después del login.
        rememberLoginDestination(entryPath);
        history.replace(loginPath());
        return false;
      }
    }

    // Continuar con el flujo normal
    if (!token || !userData) {
      setAuthenticated(false);
      setLoading(false);
      return false;
    }
    if (userData) setUserData(userData);
    setAuthenticated(true);
    localStorage.removeItem(`CIDData_${userData.CID}`);
    let cidInfo = await fetchData("CIDData", userData.CID);
    if (cidInfo.finish) setBookingReady([...BookingReady, cidInfo.IDInbox]);
    if (cidInfo.policy_id) {
      let policyInfo = await fetchData("PolicyData", cidInfo.policy_id);
      cidInfo.policyInfo = policyInfo;
    }
    setCIDData(cidInfo);

    const localPaxData = JSON.parse(
      localStorage.getItem(`LocalPaxData_${userData.CID}`)
    );
    if (localPaxData)
      setLocalPaxDataInternal(sanitizeLocalPaxData(localPaxData));
    const localCIDSignature = JSON.parse(
      localStorage.getItem(`CIDSignature_${userData.CID}`)
    );
    if (localCIDSignature) setCIDSignatureInternal(localCIDSignature);
    getCountriesData();
    getSireCities();
    setLoading(false);

    return true;
  }

  async function confirmBooking() {
    if (!CIDSignature[CIDData.IDInbox]) {
      toast.error("NO HAY FIRMA");
      return false;
    }
    const url = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_CONFIRM_BOOKING}`;
    const formData = new FormData();
    const blob = await (await fetch(CIDSignature[CIDData.IDInbox])).blob();
    formData.append("image", blob);
    const config = {
      headers: {
        "content-type": "multipart/form-data"
      }
    };
    return httpService
      .post(url, formData, config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        onAuthFailure(err, [401]);
        throw new Error(err);
      });
  }

  const doLogout = () => {
    const finish = () => {
      clearSession();
      localStorage.clear();
      setPendingNotice({ code: "logged_out" });
      setAuthenticated(false);
      // Navegación completa al login (con el prefijo de idioma): descarta el
      // estado de React con datos del huésped.
      hardNavigate(loginPath());
    };
    // Best effort: se invalida el token en el servidor antes de borrarlo. Si
    // falla o tarda, la sesión local se cierra igual.
    if (!getToken() || !process.env.REACT_APP_LOGOUT_EP) {
      finish();
      return;
    }
    httpService
      .post(
        `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_LOGOUT_EP}`,
        {},
        { skipErrorToast: true, timeout: 4000 }
      )
      .catch(err => {
        reportErrorToNewRelic("LOGOUT_EP", err);
      })
      .then(finish);
  };

  async function getSireCities() {
    let sireCities = await fetchData("SIRECities");
    if (sireCities) {
      setSIRECities(sireCities);
    }
  }

  async function doAuth(data, fetchMenu) {
    // Los errores (con su `code`) los maneja quien llama: LoginPage los mapea
    // a un mensaje claro.
    let auth = await AuthService.loginBooking(data);
    if (!auth || !auth.token) {
      throw new Error((auth && auth.message) || "invalid_credentials");
    }
    if (auth.response == 0)
      throw new Error("No existe ninguna reserva para ese apellido");
    setToken(auth.token);
    sessionExpired.current = false;
    setAuthNotice(null);
    localStorage.removeItem(`CIDData_${data.reserva}`);
    let cidInfo = await fetchData("CIDData", data.reserva);
    // El huésped puede entrar con el código del canal (Airbnb, Booking): la
    // sesión sigue con el número interno de la reserva, que es el que usan la
    // ruta /checkin/:cid, los borradores y el resto de los endpoints.
    const userData = {
      CID: cidInfo && cidInfo.IDInbox ? String(cidInfo.IDInbox) : data.reserva,
      LastName: data.apellido,
      CompanyID: data.companyid
    };
    // AuthService guardó lo que escribió el huésped; al recargar la sesión
    // tiene que seguir con el número interno.
    setStoredUserData(userData);
    setUserData(userData);
    if (cidInfo.policy_id) {
      let policyInfo = await fetchData("PolicyData", cidInfo.policy_id);
      cidInfo.policyInfo = policyInfo;
    }
    setCIDData(cidInfo);
    if (cidInfo) setAuthenticated(true);
    getCountriesData();
    getSireCities();
    return { auth, userData };
  }

  // `data` = { cid, company_id, captcha_token? }. Rechaza ante cualquier
  // error (captcha, 429...) para que LoginPage muestre el mensaje del `code`.
  async function doRequestMagicLink(data) {
    const url = `${process.env.REACT_APP_API2_EP}${process.env.REACT_APP_MAGIC_LINK}`;
    return httpService.post(url, data, { skipErrorToast: true }).catch(err => {
      reportErrorToNewRelic("doRequestMagicLink", err);
      throw err;
    });
  }

  async function fetchProductData(productId) {
    try {
      let productData = await fetchData("ProductData", productId);
      if (!productData || Object.keys(productData).length === 0) {
        throw new Error("Invalid or empty product data");
      }
      setProductData(productData);
      return productData;
    } catch (error) {
      console.error("Error fetching product data");
      toast.error(<FormattedMessage id="error.fetchProductData" />);
      return null;
    }
  }

  return (
    <GeneralContext.Provider
      value={{
        Authenticated,
        Loading,
        GeneralError,
        ProductData,
        PosData,
        CIDData,
        UserData,
        MenuData,
        setMenuData,
        fetchData,
        setPaxsData,
        PaxsData,
        LocalPaxData,
        setLocalPaxData,
        setCIDData,
        CIDData,
        doAuth,
        doLogout,
        ReviewMode,
        setReviewMode,
        fileUpload,
        submitPaxData,
        Validating,
        setValidating,
        DocTypes,
        CIDSignature,
        setCIDSignature,
        confirmBooking,
        BookingReady,
        setBookingReady,
        PreviousPage,
        setPreviousPage,
        CurrentLng,
        LngLink,
        getCountriesData,
        CountriesData,
        cancelBookingFn,
        createEmptyPax,
        doRequestMagicLink,
        SIRECities,
        fetchProductData,
        setAuthenticated,
        getMaskedHint,
        CurrentPos,
        AuthNotice,
        setAuthNotice,
        LoginPrefill,
        setLoginPrefill
      }}
    >
      {props.children}
    </GeneralContext.Provider>
  );
};

export default GeneralContextProvider;
